import * as THREE from "three";

/**
 * The wind field — what makes a meadow read as WEATHER rather than as wobbling
 * geometry.
 *
 * The flora already swayed before this, but each plant swayed on its own clock:
 * a per-instance phase into a sine, with x and z on independent waves. That has
 * no direction (a plant leans north-east on Tuesday and south-west a second
 * later) and no coherence (neighbours are unrelated), so a field of it shimmers
 * instead of blowing. What sells wind is the two things that model was missing:
 *
 *   DIRECTION. Everything leans the same way at once, along a mean flow that
 *   comes from the actual weather — the same `weatherAt()` vector that already
 *   slants the rain, so a downpour and the grass under it agree.
 *
 *   GUST FRONTS. A handful of gust CELLS ride downwind faster than the mean
 *   flow. Each is a soft band — sharp at its leading edge, trailing off behind —
 *   and where one passes, the grass lies over and then rings as it recovers.
 *   Watching a front cross a meadow is the whole effect; it is why the cells
 *   advect rather than just pulsing in place.
 *
 * FRAME. Everything is evaluated in PLANET-LOCAL space, which is the frame the
 * scatter meshes are already parented into. That matters twice over: the field
 * stays nailed to the ground as the planet spins under the floating origin, and
 * the coordinates stay near planet-radius scale (~1e4) instead of the render
 * frame's ~1e5, which a value noise cannot afford.
 *
 * A gust cell's geometry is expressed in a tangent basis (downwind, crosswind)
 * taken at the player. On a sphere that is a local flat approximation, which is
 * exactly right here — gusts only matter within a few hundred metres, and over
 * that distance a 15 km-radius planet is flat.
 */

/** Live tuning — `__hl.wind` in the console. */
export const WIND_TUNING = {
  /** Master multiplier on how far plants lay over. */
  gain: 1,
  /** Gust cell strength. 0 leaves a steady, directional breeze with no fronts. */
  gust: 1,
  /** How hard the player parts grass they walk through. */
  push: 1,
  /** Metres per second at the strongest weather. */
  maxSpeed: 11,
};

/**
 * Gust cells. Five is enough to keep fronts arriving; each costs ~10 ALU per vertex.
 *
 * Their geometry is tuned to OUR visible grass band, not to the reference's.
 * That pen renders a meadow hundreds of metres deep, so it can scatter cells
 * across ±450 m of crosswind and let most of them miss you — you still watch
 * them cross the far field. Our ground cover fades out inside ~100 m, so a cell
 * that misses by 300 m is a gust nobody ever sees. These are clustered nearer
 * and spaced closer along the wind, which puts a front through the visible grass
 * every ten seconds or so.
 */
const CELLS = 5;
/** The turbulence advection distance is wrapped here so a long session cannot
 *  drift the noise coordinate out of float precision. */
const ADV_WRAP = 8192;

interface Cell {
  /** Station along the wind axis, in planet-local metres. */
  s: number;
  /** Offset across it. */
  c: number;
  len: number;
  wid: number;
  amp: number;
  /** How far this cell veers the flow, radians. */
  veer: number;
}

/** Shared uniform objects — one set, referenced by every flora material. */
export const WIND_UNIFORMS = {
  uWindFwd: { value: new THREE.Vector3(1, 0, 0) },
  uWindSide: { value: new THREE.Vector3(0, 0, 1) },
  uWindSpeed: { value: 0 },
  uWindAdv: { value: 0 },
  uWindTime: { value: 0 },
  uWindCellA: { value: Array.from({ length: CELLS }, () => new THREE.Vector4()) },
  uWindCellB: { value: Array.from({ length: CELLS }, () => new THREE.Vector4()) },
  /** Planet-local player position, for parting the grass they walk through. */
  uWindPlayer: { value: new THREE.Vector3(0, 1e9, 0) },
  uWindPush: { value: 0 },
  /** Radius (metres) the parting reaches. A walker clears a body's width; a
   * speeder tearing past flattens a whole swath, so this is driven per-frame by
   * whatever is actually moving through the grass. */
  uWindPushRadius: { value: 1.6 },
  /** Master bend multiplier — 0 is still air, and the honest A/B. */
  uWindGain: { value: 1 },
  /**
   * Planet-local origin the along/across coordinates are measured FROM.
   *
   * This is not an optimisation, it is the whole reason the field works on a
   * sphere. The reference is a flat world, where a point's along-wind station is
   * just `dot(pos, fwd)`. Here `fwd` and `side` are TANGENT to the planet and a
   * point's position vector is RADIAL, so that dot product is identically zero —
   * every sample lands on the same station, every gust cell is either wholly on
   * or wholly off, and the field degenerates to a steady breeze. Measuring the
   * displacement from an anchor on the surface gives real tangent-plane
   * coordinates, and keeps them a few hundred metres wide instead of planet-radius.
   */
  uWindAnchor: { value: new THREE.Vector3() },
};

const _fwd = new THREE.Vector3();
const _side = new THREE.Vector3();
const _up = new THREE.Vector3();
const _rel = new THREE.Vector3();
const _shift = new THREE.Vector3();

class FloraWind {
  private cells: Cell[] = [];
  private speed = 0;
  private adv = 0;
  private time = 0;
  private seeded = false;
  private pushCenter = false;
  /** Surface point the tangent coordinates are measured from — see uWindAnchor. */
  private readonly anchor = new THREE.Vector3();
  /** Smoothed so a weather sample stepping between columns does not snap the field. */
  private smoothSpeed = 0;
  private readonly smoothDir = new THREE.Vector3(1, 0, 0);

  /**
   * Seed the cells around the player.
   *
   * BOTH coordinates are player-relative, and the cross one is the easy thing to
   * forget: the stations live in PLANET-LOCAL along/across metres, which near a
   * 15 km-radius planet are in the tens of thousands. Cells seeded at across = 0
   * are therefore fifteen kilometres off to one side, where the cross-wind
   * falloff evaluates to exactly zero — a wind field with a mean flow and no
   * gusts, which is precisely what the first run of `_grasswind.mjs` measured.
   *
   * The stations also straddle the player rather than all starting upwind, so a
   * front is already crossing the meadow on the first frame instead of taking
   * two minutes to arrive.
   */
  private reseed(playerAlong: number, playerAcross: number, rand: () => number): void {
    this.cells.length = 0;
    for (let i = 0; i < CELLS; i++) {
      this.cells.push({
        s: playerAlong - 400 + i * 160 + rand() * 100,
        c: playerAcross + (rand() - 0.5) * 260,
        len: 26 + rand() * 34,
        wid: 90 + rand() * 140,
        amp: 0.85 + rand() * 1.35,
        veer: (rand() - 0.5) * 0.42,
      });
    }
    this.seeded = true;
  }

  /**
   * Advance the field. `localPos` is the player's PLANET-LOCAL position and
   * `dirLocal` the planet-local tangent the air travels toward (weatherAt's
   * wind vector); `speed01` is its 0..1 strength. Pass speed01 = 0 off-planet.
   */
  update(dt: number, localPos: THREE.Vector3, dirLocal: THREE.Vector3, speed01: number): void {
    this.time += dt;
    WIND_UNIFORMS.uWindTime.value = this.time;

    const target = Math.max(0, Math.min(1, speed01)) * WIND_TUNING.maxSpeed;
    // Ease both magnitude and direction: the weather column is sampled at the
    // player and steps as they walk, and an unsmoothed field snaps the whole
    // meadow sideways when it does.
    const k = 1 - Math.exp(-dt / 2.5);
    this.smoothSpeed += (target - this.smoothSpeed) * k;
    if (dirLocal.lengthSq() > 1e-8) {
      this.smoothDir.lerp(dirLocal, 1 - Math.exp(-dt / 4)).normalize();
    }
    this.speed = this.smoothSpeed;
    WIND_UNIFORMS.uWindSpeed.value = this.speed;
    WIND_UNIFORMS.uWindGain.value = Math.max(0, WIND_TUNING.gain);

    // Tangent basis at the player: downwind, and across it.
    _up.copy(localPos);
    if (_up.lengthSq() < 1e-8) _up.set(0, 1, 0);
    _up.normalize();
    _fwd.copy(this.smoothDir).addScaledVector(_up, -this.smoothDir.dot(_up));
    if (_fwd.lengthSq() < 1e-8) {
      // Calm, or the wind vector happens to be parallel to up — any tangent will do.
      _fwd.set(1, 0, 0).addScaledVector(_up, -_up.x);
      if (_fwd.lengthSq() < 1e-8) _fwd.set(0, 0, 1).addScaledVector(_up, -_up.z);
    }
    _fwd.normalize();
    _side.crossVectors(_up, _fwd).normalize();
    WIND_UNIFORMS.uWindFwd.value.copy(_fwd);
    WIND_UNIFORMS.uWindSide.value.copy(_side);

    // Turbulence rides downwind with the flow. Wrapped, so the coordinate cannot
    // wander out of float precision over a long session.
    this.adv = (this.adv + this.speed * dt) % ADV_WRAP;
    WIND_UNIFORMS.uWindAdv.value = this.adv;

    // Rebase the anchor when the player has walked a long way from it, sliding
    // every cell station by the same displacement so nothing jumps.
    if (!this.seeded) {
      this.anchor.copy(localPos);
    } else if (localPos.distanceToSquared(this.anchor) > 2000 * 2000) {
      _shift.copy(localPos).sub(this.anchor);
      const dA = _shift.dot(_fwd);
      const dC = _shift.dot(_side);
      for (const c of this.cells) { c.s -= dA; c.c -= dC; }
      this.anchor.copy(localPos);
    }
    WIND_UNIFORMS.uWindAnchor.value.copy(this.anchor);

    _rel.copy(localPos).sub(this.anchor);
    const playerAlong = _rel.dot(_fwd);
    const playerAcross = _rel.dot(_side);
    if (!this.seeded) this.reseed(playerAlong, playerAcross, Math.random);

    // Cells ride downwind faster than the mean flow — that speed difference IS
    // the gust front sweeping past you.
    const advance = Math.max(this.speed, 1.5) * 1.25 * dt;
    for (const c of this.cells) {
      c.s += advance;
      // Recycled once a cell has passed well downwind of the player, and
      // re-thrown far enough upwind that it fades in off screen.
      if (c.s - playerAlong > 260) {
        c.s -= 700 + Math.random() * 160;
        c.c = playerAcross + (Math.random() - 0.5) * 280;
        c.len = 26 + Math.random() * 34;
        c.wid = 90 + Math.random() * 140;
        c.amp = 0.8 + Math.random() * 1.4;
        c.veer = (Math.random() - 0.5) * 0.44;
      }
    }
    const gustK = WIND_TUNING.gust;
    for (let i = 0; i < CELLS; i++) {
      const c = this.cells[i];
      WIND_UNIFORMS.uWindCellA.value[i].set(c.s, c.c, c.len, c.wid);
      WIND_UNIFORMS.uWindCellB.value[i].set(c.amp * gustK, c.veer, 0, 0);
    }

    // The wind FIELD always follows the player (it is their weather column), but
    // what PARTS the grass may be something else they are riding.
    if (!this.pushCenter) WIND_UNIFORMS.uWindPlayer.value.copy(localPos);
  }

  /** Part the grass around this planet-local point instead of the player, or
   * null to go back to following them. */
  setPushCenter(localPos: THREE.Vector3 | null): void {
    this.pushCenter = localPos !== null;
    if (localPos) WIND_UNIFORMS.uWindPlayer.value.copy(localPos);
  }

  /**
   * Grass parts around whatever is moving through it, while that thing is
   * actually touching the ground. `radius` is metres — a walker is ~1.6, a
   * vehicle is its own width plus a margin, so a speeder lays down a swath
   * instead of a footprint.
   */
  setPush(on: boolean, radius = 1.6, strength = 1): void {
    WIND_UNIFORMS.uWindPush.value = on ? WIND_TUNING.push * strength : 0;
    WIND_UNIFORMS.uWindPushRadius.value = Math.max(0.4, radius);
  }

  /** Current mean speed, m/s — for audio and anything else that wants it. */
  get meanSpeed(): number {
    return this.speed;
  }
}

export const floraWind = new FloraWind();

/** Declarations + the field evaluator, injected into every flora vertex shader. */
export const WIND_GLSL = /* glsl */ `
uniform vec3 uWindFwd;
uniform vec3 uWindSide;
uniform float uWindSpeed;
uniform float uWindAdv;
uniform float uWindTime;
uniform vec4 uWindCellA[${CELLS}];
uniform vec4 uWindCellB[${CELLS}];
uniform vec3 uWindPlayer;
uniform float uWindPush;
uniform float uWindPushRadius;
uniform vec3 uWindAnchor;
uniform float uWindGain;

float hlWindHash(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}
float hlWindNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hlWindHash(i), hlWindHash(i + vec2(1.0, 0.0)), u.x),
             mix(hlWindHash(i + vec2(0.0, 1.0)), hlWindHash(i + vec2(1.0, 1.0)), u.x), u.y);
}

/**
 * The field at a planet-local point. xyz is the wind vector (direction AND
 * magnitude, in the tangent plane); w is the gust strength, which the caller
 * uses for the quiver a passing front leaves behind.
 */
vec4 hlWindAt(vec3 lp) {
  // DISPLACEMENT from the anchor, not the raw position: fwd/side are tangent to
  // the planet, so dotting them with a radial position gives zero. See uWindAnchor.
  vec3 rel = lp - uWindAnchor;
  float along = dot(rel, uWindFwd);
  float across = dot(rel, uWindSide);

  // Gust cells: a sharp leading edge (smoothstep on the head) with an
  // exponential tail behind it, tapered across the wind. Summing a few of these
  // gives fronts that arrive, pass and overlap.
  float gust = 0.0;
  float veer = 0.0;
  for (int i = 0; i < ${CELLS}; i++) {
    vec4 A = uWindCellA[i];
    float u = (along - A.x) / max(A.z, 1e-3);
    if (u > 0.16 || u < -6.0) continue;
    float head = smoothstep(0.14, 0.0, u);
    float body = exp(u * 2.05);
    float cw = exp(-pow(abs(across - A.y) / max(A.w * 0.5, 1e-3), 2.3));
    float g = uWindCellB[i].x * head * body * cw;
    gust += g;
    veer += g * uWindCellB[i].y;
  }

  // Two octaves of turbulence, advected downwind so the texture of the flow
  // travels with it rather than sitting still in the world.
  vec2 q = vec2(along - uWindAdv, across) * 0.0125;
  float turb = (hlWindNoise(q) - 0.5) + (hlWindNoise(q * 2.6 + 11.0) - 0.5) * 0.79;

  float sp = uWindSpeed * (1.0 + gust * 0.85) * (1.0 + turb * 0.30);
  float ca = cos(veer), sa = sin(veer);
  return vec4((uWindFwd * ca + uWindSide * sa) * sp, gust);
}
`;
